import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import {
  DURUM_ETIKETI,
  izinBakiyesi,
  izinKullanmayanlar,
  izinListesi,
  type IzinDurumu,
  type IzinKullanmayanSatiri,
  type IzinSorgusu,
  type IzinTalebiOzeti,
} from '../api/izin';
import type { Kimlik } from '../api/tipler';
import { BirimSecici } from '../bilesenler/BirimSecici';
import { Diyalog } from '../bilesenler/Diyalog';
import { BakiyeListesi, DurumRozeti, SinirRozeti, SureHucresi } from '../bilesenler/IzinParcalari';
import { Sayfalama, Tablo, type Sutun } from '../bilesenler/Tablo';
import { Dugme, HataKutusu, Metin, Sekme } from '../bilesenler/temel';
import { tarihMetni, zamanMetni } from '../bicim/tarih';
import { sayfalamaKullan } from '../kancalar/sayfalama';
import { yetkiKullan, yetkilerHazirKullan } from '../yetki/yetki';
import { Izinlerim, IzinOnay } from './IzinOnay';

/**
 * İZİN TALEPLERİ.
 *
 * ALTI YETKİ SEVİYESİ VAR ve şef ile müdür onayı AYRI KODLAR
 * (`IZIN_ONAY_ILK` / `IZIN_ONAY_SON`). Tek değişkenle yazılsaydı şef "Müdür
 * onayı" düğmesine basıp 403 alırdı.
 *
 * `POST /izin` ve `POST /izin/on-kontrol` BAĞLI DEĞİL: ikisi de `izinTipiId`
 * istiyor ama izin tipi kataloğu için uç yok ve id'ler sabit değil. İkisi de
 * `IZIN_TALEP` istiyor, yani PWA işi (`api/izin.ts` başındaki nota bakın).
 */

const DURUMLAR: IzinDurumu[] = [
  'taslak',
  'sef_onayi',
  'mudur_onayi',
  'onaylandi',
  'reddedildi',
  'iptal',
];

type SekmeKodu = 'talepler' | 'onay' | 'benim';

export function Izin() {
  const yetkilerHazir = yetkilerHazirKullan();

  const listeGorebilir = yetkiKullan('PERSONEL_GORUNTULE', 'IZIN_ONAY_ILK', 'IZIN_ONAY_SON');
  const onayGorebilir = yetkiKullan('IZIN_ONAY_ILK', 'IZIN_ONAY_SON');
  const talepEdebilir = yetkiKullan('IZIN_TALEP');

  const [sekmeSecimi, sekmeSecimiYaz] = useState<SekmeKodu | null>(null);
  // Sekme türetilir; yetkiler asenkron geliyor (SÖZLEŞME §5).
  const sekme: SekmeKodu =
    sekmeSecimi ?? (listeGorebilir ? 'talepler' : onayGorebilir ? 'onay' : 'benim');

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-xl font-semibold text-slate-900">İzin talepleri</h1>
        <p className="mt-1 text-sm text-slate-600">
          İKİ BASAMAKLI ONAY: eşzamanlı izin sınırı aşılıyorsa önce şefe, aşılmıyorsa doğrudan
          müdüre gider. Taslak talepler plana yansımaz.
        </p>
      </header>

      <div className="flex gap-1 border-b border-slate-200">
        {listeGorebilir && (
          <Sekme
            etkin={sekme === 'talepler'}
            tikla={() => sekmeSecimiYaz('talepler')}
            etiket="Talepler"
          />
        )}
        {onayGorebilir && (
          <Sekme
            etkin={sekme === 'onay'}
            tikla={() => sekmeSecimiYaz('onay')}
            etiket="Onay bekleyenler"
          />
        )}
        {talepEdebilir && (
          <Sekme
            etkin={sekme === 'benim'}
            tikla={() => sekmeSecimiYaz('benim')}
            etiket="Kendi izinlerim"
          />
        )}
      </div>

      {!yetkilerHazir ? (
        <p className="text-sm text-slate-500">Yetkiler yükleniyor…</p>
      ) : sekme === 'talepler' && listeGorebilir ? (
        <Talepler />
      ) : sekme === 'onay' && onayGorebilir ? (
        <IzinOnay yetkilerHazir={yetkilerHazir} />
      ) : talepEdebilir ? (
        <Izinlerim yetkilerHazir={yetkilerHazir} />
      ) : (
        <p className="rounded-lg border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
          Bu ekranı görüntüleme yetkiniz yok.
        </p>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Talepler — sayfalı liste                                            */
/* ------------------------------------------------------------------ */

interface TalepSuzgeci {
  birimId?: Kimlik;
  durum?: IzinDurumu;
  baslangic?: string;
  bitis?: string;
}

function Talepler() {
  /*
   * `sayfalamaKullan` İLK GERÇEK KULLANIMI.
   *
   * Süzgeç yazımı sayfayı 1'e alır; alınmasaydı 4. sayfadayken birim
   * değiştiren kullanıcı boş liste görür ve bunu "kayıt yok" sanardı.
   */
  const { suzgec, suzgeciYaz, sayfa, sayfayaGit, sayfaBoyutu } = sayfalamaKullan<TalepSuzgeci>({});

  const [acikTalep, acikTalepYaz] = useState<IzinTalebiOzeti | null>(null);
  const [kullanmayanlarAcik, kullanmayanlarAcikYaz] = useState(false);

  const kullanmayanlarGorebilir = yetkiKullan('IZIN_ONAY_SON', 'RAPOR_GORUNTULE');

  const sorgu: IzinSorgusu = { ...suzgec, sayfa, sayfaBoyutu };

  const liste = useQuery({
    queryKey: ['izin', 'liste', sorgu],
    queryFn: () => izinListesi(sorgu),
    // Sayfa değişince tablo boşalıp zıplamasın.
    placeholderData: keepPreviousData,
  });

  const sutunlar: Sutun<IzinTalebiOzeti>[] = [
    {
      baslik: 'Personel',
      hucre: (t) => (
        <span>
          <span className="block font-medium text-slate-900">{t.adSoyad}</span>
          <span className="block text-xs text-slate-500">
            {t.personelNo} · {t.birimAd}
          </span>
        </span>
      ),
    },
    { baslik: 'İzin tipi', hucre: (t) => t.izinTipiAd },
    {
      baslik: 'Tarih',
      hucre: (t) => (
        <span className="text-xs">
          {tarihMetni(t.baslangicTarihi)} – {tarihMetni(t.bitisTarihi)}
          {t.baslangicSaat !== null && (
            <span className="block text-slate-500">
              {t.baslangicSaat} – {t.bitisSaat}
            </span>
          )}
        </span>
      ),
      daralt: true,
    },
    { baslik: 'Süre', hucre: (t) => <SureHucresi talep={t} />, daralt: true, hizala: 'sag' },
    {
      baslik: 'Durum',
      hucre: (t) => (
        <span className="flex items-center gap-1.5">
          <DurumRozeti durum={t.durum} />
          {t.sinirAsimiMi && <SinirRozeti />}
        </span>
      ),
      daralt: true,
    },
    {
      baslik: '',
      hucre: (t) => (
        <button
          type="button"
          onClick={() => acikTalepYaz(t)}
          className="rounded px-2 py-1 text-xs text-kurum-700 hover:bg-kurum-50"
        >
          Ayrıntı
        </button>
      ),
      hizala: 'sag',
      daralt: true,
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-64">
          <BirimSecici
            deger={suzgec.birimId}
            degisti={(yeni) => suzgeciYaz((o) => ({ ...o, birimId: yeni }))}
            bosSecenek="Kapsamımdaki tüm birimler"
          />
        </div>

        <label className="text-sm text-slate-700">
          <span className="mb-1 block font-medium">Durum</span>
          <select
            value={suzgec.durum ?? ''}
            onChange={(olay) =>
              suzgeciYaz((o) => ({
                ...o,
                durum: olay.target.value === '' ? undefined : (olay.target.value as IzinDurumu),
              }))
            }
            className="rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-kurum-500 focus:ring-2 focus:ring-kurum-100"
          >
            <option value="">Tümü</option>
            {DURUMLAR.map((d) => (
              <option key={d} value={d}>
                {DURUM_ETIKETI[d]}
              </option>
            ))}
          </select>
        </label>

        <Metin
          etiket="Bitişi bundan sonra"
          tur="date"
          deger={suzgec.baslangic ?? ''}
          degisti={(yeni) =>
            suzgeciYaz((o) => ({ ...o, baslangic: yeni === '' ? undefined : yeni }))
          }
        />
        <Metin
          etiket="Başlangıcı bundan önce"
          tur="date"
          deger={suzgec.bitis ?? ''}
          degisti={(yeni) => suzgeciYaz((o) => ({ ...o, bitis: yeni === '' ? undefined : yeni }))}
        />
      </div>

      <HataKutusu hata={liste.error} />

      <Tablo
        sutunlar={sutunlar}
        satirlar={liste.data?.kayitlar ?? []}
        anahtar={(t) => t.izinTalebiId}
        bekliyor={liste.isPending}
        bosMetin="Bu süzgeçle izin talebi bulunamadı."
      />

      <Sayfalama
        sayfa={sayfa}
        sayfaBoyutu={sayfaBoyutu}
        toplam={liste.data?.toplam ?? 0}
        sayfayaGit={sayfayaGit}
        bekliyor={liste.isFetching}
      />

      {kullanmayanlarGorebilir && (
        <section className="rounded-lg border border-slate-200 bg-white">
          <button
            type="button"
            onClick={() => kullanmayanlarAcikYaz((o) => !o)}
            className="flex w-full items-center justify-between px-4 py-3 text-left text-sm font-medium text-slate-800 hover:bg-slate-50"
          >
            İzin kullanmayan personel (4857/53)
            <span className="text-xs text-slate-500">
              {kullanmayanlarAcik ? 'gizle' : 'göster'}
            </span>
          </button>
          {kullanmayanlarAcik && <Kullanmayanlar />}
        </section>
      )}

      {acikTalep !== null && <TalepAyrintisi talep={acikTalep} kapat={() => acikTalepYaz(null)} />}
    </div>
  );
}

/* ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ */

function Kullanmayanlar() {
  const liste = useQuery({
    queryKey: ['izin', 'kullanmayanlar'],
    queryFn: () => izinKullanmayanlar(),
  });

  const sutunlar: Sutun<IzinKullanmayanSatiri>[] = [
    {
      baslik: 'Personel',
      hucre: (s) => (
        <span>
          <span className="block font-medium text-slate-900">{s.adSoyad}</span>
          <span className="block text-xs text-slate-500">
            {s.personelNo} · {s.birimAd}
          </span>
        </span>
      ),
    },
    { baslik: 'Kıdem', hucre: (s) => `${s.kidemYili} yıl`, daralt: true, hizala: 'sag' },
    {
      baslik: 'Birikmiş bakiye',
      hucre: (s) => <span className="font-medium text-amber-800">{s.toplamBakiye} gün</span>,
      daralt: true,
      hizala: 'sag',
    },
    {
      baslik: 'Kullanım oranı',
      // Oran da METİNDİR; `%` eklemek dışında dokunulmaz.
      hucre: (s) => (s.kullanimOrani === null ? '—' : `%${s.kullanimOrani}`),
      daralt: true,
      hizala: 'sag',
    },
  ];

  return (
    <div className="space-y-2 border-t border-slate-200 p-4">
      <p className="text-xs text-slate-500">
        Biriken izin İKİ TARAFLI ZARARDIR: personel dinlenmiyor, kurum çıkışta toplu tazminat
        ödüyor. İzin kullandırma yükümlülüğü işverendedir.
      </p>
      <HataKutusu hata={liste.error} />
      <Tablo
        sutunlar={sutunlar}
        satirlar={liste.data ?? []}
        anahtar={(s) => s.personelId}
        bekliyor={liste.isPending}
        bosMetin="İzin kullanmayan personel yok."
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Talep ayrıntısı                                                     */
/* ------------------------------------------------------------------ */

function TalepAyrintisi({ talep, kapat }: { talep: IzinTalebiOzeti; kapat: () => void }) {
  const bakiye = useQuery({
    queryKey: ['izin', 'bakiye', talep.personelId],
    queryFn: () => izinBakiyesi(talep.personelId),
  });

  return (
    <Diyalog
      baslik={`${talep.adSoyad} — ${talep.izinTipiAd}`}
      aciklama={`${tarihMetni(talep.baslangicTarihi)} – ${tarihMetni(talep.bitisTarihi)} · ${talep.personelNo} · ${talep.birimAd}`}
      kapat={kapat}
      genis
      cocuk={
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <DurumRozeti durum={talep.durum} />
            {talep.sinirAsimiMi && (
              <span className="rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-800">
                Eşzamanlı izin sınırı aşıldı
              </span>
            )}
          </div>

          <dl className="grid gap-3 text-sm sm:grid-cols-3">
            <Kutu etiket="İş günü" deger={`${talep.isGunuSayisi} gün`} />
            <Kutu etiket="Takvim günü" deger={`${talep.takvimGunuSayisi} gün`} />
            {talep.saatSayisi !== null && <Kutu etiket="Saat" deger={`${talep.saatSayisi} saat`} />}
          </dl>

          <dl className="grid gap-3 text-sm sm:grid-cols-2">
            {/*
              ÜÇÜ DE `timestamptz` — `zamanMetni`, `tarihMetni` DEĞİL
              (SÖZLEŞME §15). `tarihMetni` metni keser: saati gizler ve
              Türkiye'de 21:00'den sonra kaydedilen bir onayı BİR ÖNCEKİ GÜN
              gösterirdi. Kolon türü migration'dan doğrulandı.
            */}
            <Kutu etiket="Talep zamanı" deger={zamanMetni(talep.talepZamani)} />
            <Kutu
              etiket="Şef onayı"
              deger={talep.sefOnayZamani === null ? '—' : zamanMetni(talep.sefOnayZamani)}
            />
            <Kutu
              etiket="Müdür onayı"
              deger={talep.mudurOnayZamani === null ? '—' : zamanMetni(talep.mudurOnayZamani)}
            />
            <Kutu etiket="Vekil" deger={talep.vekilAtamaId === null ? '—' : talep.vekilAtamaId} />
          </dl>

          {talep.gerekce !== null && <Bolum baslik="Talep gerekçesi" metin={talep.gerekce} />}
          {talep.redGerekcesi !== null && (
            <Bolum baslik="Red gerekçesi" metin={talep.redGerekcesi} vurgula />
          )}

          <section>
            <h3 className="text-sm font-semibold text-slate-800">İzin bakiyesi</h3>
            <HataKutusu hata={bakiye.error} />
            {bakiye.isPending ? (
              <p className="mt-1 text-sm text-slate-500">Yükleniyor…</p>
            ) : (
              <BakiyeListesi satirlar={bakiye.data ?? []} />
            )}
          </section>

          <div className="flex justify-end">
            <Dugme tur="ikincil" tikla={kapat} cocuk="Kapat" />
          </div>
        </div>
      }
    />
  );
}

function Kutu({ etiket, deger }: { etiket: string; deger: string }) {
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
      <dt className="text-xs text-slate-500">{etiket}</dt>
      <dd className="mt-0.5 font-medium text-slate-900">{deger}</dd>
    </div>
  );
}

function Bolum({
  baslik,
  metin,
  vurgula = false,
}: {
  baslik: string;
  metin: string;
  vurgula?: boolean;
}) {
  return (
    <section
      className={`rounded-md border p-3 ${
        vurgula ? 'border-red-200 bg-red-50' : 'border-slate-200 bg-white'
      }`}
    >
      <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-600">{baslik}</h3>
      <p className="mt-1 whitespace-pre-line text-sm text-slate-800">{metin}</p>
    </section>
  );
}
